import { useEffect, useState } from "react";
import type { ReactNode } from "react";
import {
  Box,
  Button,
  Flex,
  Grid,
  HStack,
  Input,
  Stack,
  Text,
} from "@chakra-ui/react";
import { useCustom, useList, useNotification } from "@refinedev/core";
import { Link, useSearchParams } from "react-router";
import {
  Activity,
  AlertTriangle,
  ArrowDown,
  ArrowUp,
  Bot,
  CircleDollarSign,
  Clock3,
  Crosshair,
  Database,
  Filter,
  Gauge,
  History,
  Layers3,
  LockKeyhole,
  Radio,
  Search,
  ShieldCheck,
  TrendingUp,
  WalletCards,
  X,
} from "lucide-react";
import { PriceChart } from "../components/charts";
import { AppSelect } from "../components/app-select";
import { DataTable, type Column } from "../components/tables";
import {
  ExecutionModePills,
  RuntimeActivityPill,
  RuntimeHeartbeatStatus,
  runtimeActivityLabel,
} from "../components/runtime-activity";
import {
  DataBar,
  EmptyState,
  MetricCard,
  Money,
  PageHeader,
  Panel,
  Percent,
  StatusPill,
} from "../components/ui";
import { apiRequest } from "../providers/api-client";
import type {
  AuditRecord,
  BotRecord,
  OrderRecord,
  PositionRecord,
} from "../types/domain";

type PortfolioSummary = {
  equity: number;
  availableEquity: number;
  todayPnl: number;
  currentDrawdown: number;
  grossExposure: number;
  netExposure: number;
  marginUsed: number;
  marginUtilization: number;
  positionCount: number;
  accountCount: number;
  maxLeverage: number;
  maxDrawdownLimit: number;
  maxDailyLossLimit: number;
  maxSessionCloseLossLimit: number;
  asOf: string;
};

type MarketSeriesPoint = {
  time: string;
  openTimeMs: number;
  closeTimeMs: number;
  open: number;
  high: number;
  low: number;
  close: number;
  volume: number;
};

type MarketSeriesResponse = {
  botId: string;
  symbol: string;
  timeframe: string;
  provider: string;
  source: "BROKER_HISTORY" | "RUNTIME_NOT_ACTIVE";
  asOf: string;
  points: MarketSeriesPoint[];
};

export function LiveMonitorPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const requestedBotId = searchParams.get("bot");
  const positionsQuery = useList<PositionRecord>({
    resource: "positions",
    pagination: { currentPage: 1, pageSize: 50 },
    liveMode: "auto",
  });
  const ordersQuery = useList<OrderRecord>({
    resource: "orders",
    pagination: { currentPage: 1, pageSize: 200 },
    liveMode: "auto",
  });
  const botsQuery = useList<BotRecord>({
    resource: "bots",
    pagination: { currentPage: 1, pageSize: 50 },
    liveMode: "auto",
    queryOptions: { refetchInterval: 2_000 },
  });
  const auditQuery = useList<AuditRecord>({
    resource: "auditLogs",
    pagination: { currentPage: 1, pageSize: 200 },
    liveMode: "auto",
  });
  const summaryQuery = useCustom<PortfolioSummary>({
    url: "/portfolio/summary",
    method: "get",
    queryOptions: { refetchInterval: 5_000 },
  });
  const positions = positionsQuery.result.data ?? [];
  const allOrders = ordersQuery.result.data ?? [];
  const bots = botsQuery.result.data ?? [];
  const allEvents = auditQuery.result.data ?? [];
  const summary = summaryQuery.query.data?.data;
  const active =
    bots.find((bot) => bot.id === requestedBotId) ??
    bots.find((bot) => bot.status === "RUNNING") ??
    bots[0];
  const orders = active
    ? allOrders.filter((order) => order.botId === active.id)
    : [];
  const events = active
    ? allEvents.filter((event) => event.botId === active.id)
    : [];

  useEffect(() => {
    if (!active || requestedBotId === active.id) return;
    const next = new URLSearchParams(searchParams);
    next.set("bot", active.id);
    setSearchParams(next, { replace: true });
  }, [active, requestedBotId, searchParams, setSearchParams]);

  const selectBot = (botId: string) => {
    const next = new URLSearchParams(searchParams);
    next.set("bot", botId);
    setSearchParams(next);
  };
  const marketSeriesQuery = useCustom<MarketSeriesResponse>({
    url: active?.id
      ? `/bots/${encodeURIComponent(active.id)}/market-series?limit=120`
      : "/bots/__inactive__/market-series?limit=120",
    method: "get",
    queryOptions: {
      enabled: Boolean(active?.id),
      refetchInterval: 30_000,
    },
  });
  const marketSeries = marketSeriesQuery.query.data?.data;
  const brokerBars =
    marketSeries && active && marketSeries.botId === active.id
      ? marketSeries.points
      : [];
  const priceSeries = brokerBars.map((point) => ({
    time: new Date(point.time).toLocaleTimeString([], {
      hour: "2-digit",
      minute: "2-digit",
    }),
    price: point.close,
  }));
  const lastBar = brokerBars[brokerBars.length - 1];
  const sessionHigh = brokerBars.length
    ? Math.max(...brokerBars.map((point) => point.high))
    : undefined;
  const sessionLow = brokerBars.length
    ? Math.min(...brokerBars.map((point) => point.low))
    : undefined;
  const feedLive = active?.runtimeActivity === "TRACKING";
  const outsideHours = active?.marketSession === "OUTSIDE_TRADING_HOURS";
  const executionLabel = active?.mode === "PAPER"
    ? "PAPER · SIMULATED ORDERS"
    : active?.mode === "LIVE"
      ? `LIVE · ${active.brokerEnvironment ?? "UNKNOWN"} ACCOUNT`
      : (active?.mode ?? "No mode");
  const feedLabel = outsideHours
    ? "MARKET OUTSIDE HOURS"
    : feedLive
      ? "FEED ACTIVE"
      : active?.status === "RUNNING"
        ? "WAITING FOR MARKET DATA"
        : "FEED INACTIVE";
  return (
    <Stack gap="4">
      <PageHeader
        title="Live Monitor"
        description="Real broker history, persisted cTrader runtime state and account risk in one operational view."
        actions={
          <>
            <AppSelect
              ariaLabel="Select monitored bot"
              value={active?.id ?? ""}
              onValueChange={selectBot}
              width={{ base: "100%", sm: "340px" }}
              disabled={!active}
              placeholder="No registered bots"
              options={bots.map((bot) => ({
                value: bot.id,
                label: `${bot.name} · ${bot.symbol} · ${bot.status}`,
              }))}
            />
            <ExecutionModePills bot={active} />
            <RuntimeActivityPill bot={active} />
            <StatusPill tone={feedLive ? "positive" : "warning"}>
              <HStack gap="1">
                <Box as={Radio} boxSize="3" />
                {feedLabel}
              </HStack>
            </StatusPill>
          </>
        }
      />
      <Grid
        templateColumns={{ base: "1fr", sm: "1fr 1fr", xl: "repeat(4, 1fr)" }}
        gap="3"
      >
        <MetricCard
          label="Instrument"
          value={`${active?.symbol ?? "—"} · ${active?.timeframe ?? marketSeries?.timeframe ?? "—"}`}
          detail={`${active?.broker ?? "No broker"} · ${executionLabel} · bot ${active ? bots.findIndex((bot) => bot.id === active.id) + 1 : 0}/${bots.length}`}
          icon={Crosshair}
        />
        <MetricCard
          label="Last broker close"
          value={formatPrice(lastBar?.close)}
          detail={lastBar ? formatTimestamp(lastBar.time) : "Waiting for real broker history"}
          icon={TrendingUp}
        />
        <MetricCard
          label="Account equity"
          value={<Money value={active?.equity ?? summary?.equity ?? 0} />}
          detail="Last persisted broker account snapshot"
          icon={WalletCards}
        />
        <MetricCard
          label="Gross exposure"
          value={`${(active?.exposure ?? 0).toFixed(2)}x`}
          detail={`Selected bot snapshot · ${active?.position ?? "FLAT"}`}
          icon={Gauge}
        />
      </Grid>
      <Grid
        templateColumns={{
          base: "1fr",
          xl: "minmax(0,1.55fr) minmax(360px,.7fr)",
        }}
        gap="4"
      >
        <Panel
          title={`${active?.symbol ?? "No symbol"} · ${active?.timeframe ?? marketSeries?.timeframe ?? "—"} broker history`}
          icon={TrendingUp}
          action={
            <StatusPill
              tone={
                brokerBars.length
                  ? "positive"
                  : marketSeriesQuery.query.isLoading
                    ? "info"
                    : "warning"
              }
            >
              {brokerBars.length
                ? `${brokerBars.length} REAL BARS`
                : marketSeriesQuery.query.isLoading
                  ? "LOADING"
                  : "UNAVAILABLE"}
            </StatusPill>
          }
        >
          {brokerBars.length ? (
            <Stack gap="3">
              <Grid templateColumns="repeat(3, minmax(0, 1fr))" gap="2">
                <MarketFact label="Range high" value={formatPrice(sessionHigh)} />
                <MarketFact label="Range low" value={formatPrice(sessionLow)} />
                <MarketFact label="Updated" value={formatTimestamp(lastBar?.time, true)} />
              </Grid>
              <PriceChart
                data={priceSeries}
                height={{ base: "260px", xl: "330px" }}
              />
              <HStack gap="2" color="fg.subtle">
                <Box as={Database} boxSize="3.5" />
                <Text fontSize="10px">
                  Direct cTrader history · no generated or mock candles
                </Text>
              </HStack>
            </Stack>
          ) : (
            <MarketSeriesEmpty
              loading={marketSeriesQuery.query.isLoading}
              feedLabel={feedLabel}
              lastMarketEvent={active?.lastMarketEvent}
              error={marketSeriesQuery.query.error?.message}
              source={marketSeries?.source}
              botStatus={active?.status}
            />
          )}
        </Panel>
        <Stack gap="4">
          <Panel title="Current bot state" icon={Bot}>
            <StateRow label="Bot" value={active?.name ?? "No registered bot"} />
            <StateRow label="Lifecycle" value={active?.status ?? "UNAVAILABLE"} />
            <StateRow label="Runtime" value={runtimeActivityLabel(active)} />
            <StateRow
              label="Heartbeat"
              value={<RuntimeHeartbeatStatus bot={active} />}
            />
            <StateRow
              label="Market subscription"
              value={active?.marketSubscriptionActive ? "ACTIVE" : "INACTIVE"}
            />
            <StateRow label="Market data" value={feedLabel} />
            <StateRow
              label="Last signal"
              value={active?.lastSignal ?? "No signal"}
            />
            <StateRow label="Position" value={active?.position ?? "FLAT"} />
            <Text mt="3" fontFamily="mono" fontSize="10px" color="fg.subtle">
              Last finalized market event:{" "}
              {active?.lastMarketEvent ??
                (outsideHours ? "market outside trading hours" : "waiting for data")}
            </Text>
          </Panel>
          <Panel title="Risk now" icon={ShieldCheck}>
            <RiskLine
              label="Gross leverage"
              value={`${(active?.exposure ?? 0).toFixed(2)}x`}
              state={`Limit ${(summary?.maxLeverage ?? 0).toFixed(2)}x`}
            />
            <RiskLine
              label="Net leverage"
              value={`${(summary?.netExposure ?? 0).toFixed(2)}x`}
              state="Signed persisted broker exposure"
            />
            <RiskLine
              label="Drawdown"
              value={`${(summary?.currentDrawdown ?? 0).toFixed(2)}%`}
              state={`Guard ${(summary?.maxDrawdownLimit ?? 0).toFixed(2)}%`}
            />
            <RiskLine
              label="Hard intraday loss"
              value={`${Math.max(0, -(summary?.todayPnl ?? 0) / Math.max(1, (summary?.equity ?? 0) - (summary?.todayPnl ?? 0)) * 100).toFixed(2)}%`}
              state={`Guard ${(summary?.maxDailyLossLimit ?? 0).toFixed(2)}%`}
            />
            {(summary?.maxSessionCloseLossLimit ?? 0) > 0 && (
              <RiskLine
                label="Session-close loss"
                value={`${Math.max(0, -(summary?.todayPnl ?? 0) / Math.max(1, (summary?.equity ?? 0) - (summary?.todayPnl ?? 0)) * 100).toFixed(2)}%`}
                state={`19:30 guard ${(summary?.maxSessionCloseLossLimit ?? 0).toFixed(2)}%`}
              />
            )}
            <RiskLine
              label="Margin utilization"
              value={`${(summary?.marginUtilization ?? 0).toFixed(2)}%`}
              state="Broker margin / equity"
            />
          </Panel>
        </Stack>
      </Grid>
      <Grid templateColumns={{ base: "1fr", xl: "1fr 1fr" }} gap="4">
        <Panel
          title="Broker account positions"
          icon={Layers3}
          action={<StatusPill>{positions.length}</StatusPill>}
        >
          <CompactPositions rows={positions} />
        </Panel>
        <Panel
          title="Selected bot orders"
          icon={History}
          action={<StatusPill>{orders.length}</StatusPill>}
        >
          <CompactOrders rows={orders} />
        </Panel>
      </Grid>
      <Grid
        templateColumns={{ base: "1fr", xl: "minmax(0,1fr) 380px" }}
        gap="4"
      >
        <Panel title="Execution safeguards" icon={ShieldCheck}>
          <Stack gap="3">
            <RiskLine
              label="Duplicate-order guard"
              value="SERVER"
              state="Durable client order IDs before dispatch"
            />
            <RiskLine
              label="Ambiguous broker result"
              value="FAIL CLOSED"
              state="Reconciliation required before retry"
            />
            <RiskLine
              label="UI refresh"
              value="5s POLL"
              state="Every refresh reads the server again"
            />
          </Stack>
        </Panel>
        <Panel title="Manual orders" icon={LockKeyhole}>
          <Text fontSize="xs" color="fg.muted">
            Manual order submission is disabled because the server exposes no
            strategy-safe manual-order endpoint. Orders can only be generated by
            the validated trading kernel.
          </Text>
        </Panel>
      </Grid>
      <Panel
        title="Selected bot events"
        icon={Activity}
        action={<StatusPill>{events.length}</StatusPill>}
      >
        <Stack gap="0">
          {events.slice(0, 10).map((event, index) => (
            <Flex
              key={event.id}
              py="2.5"
              borderBottomWidth={
                index === Math.min(events.length, 10) - 1 ? "0" : "1px"
              }
              borderColor="border.subtle"
              gap="3"
            >
              <Box
                boxSize="7px"
                bg={
                  event.severity === "error"
                    ? "status.negative"
                    : event.severity === "warning"
                      ? "status.warning"
                      : "status.info"
                }
                borderRadius="999px"
                mt="1.5"
              />
              <Box>
                <Text fontSize="xs" fontFamily="mono">
                  {new Date(event.time).toLocaleString()} · {event.eventType}
                </Text>
                <Text fontSize="10px" color="fg.muted">
                  {event.summary}
                </Text>
              </Box>
            </Flex>
          ))}
          {events.length === 0 && (
            <MonitorEmpty
              icon={Clock3}
              title="No persisted runtime events"
              description="Runtime events will appear here after the server records them."
            />
          )}
          {events.length > 10 && (
            <Text pt="3" color="fg.subtle" fontSize="10px">
              Showing the latest 10 of {events.length} events for this bot.
            </Text>
          )}
        </Stack>
      </Panel>
    </Stack>
  );
}

function MarketFact({ label, value }: { label: string; value: string }) {
  return (
    <Box bg="bg.subtle" borderRadius="4px" p="3" minW="0">
      <Text fontSize="10px" color="fg.muted">
        {label}
      </Text>
      <Text mt="1" fontFamily="mono" fontSize="xs" fontWeight="700" truncate>
        {value}
      </Text>
    </Box>
  );
}

function MarketSeriesEmpty({
  loading,
  feedLabel,
  lastMarketEvent,
  error,
  source,
  botStatus,
}: {
  loading: boolean;
  feedLabel: string;
  lastMarketEvent?: string;
  error?: string;
  source?: MarketSeriesResponse["source"];
  botStatus?: BotRecord["status"];
}) {
  const runtimeInactive = source === "RUNTIME_NOT_ACTIVE";
  return (
    <Flex
      minH={{ base: "260px", xl: "330px" }}
      align="center"
      justify="center"
      textAlign="center"
    >
      <Stack maxW="440px" gap="3" align="center">
        <Flex
          boxSize="44px"
          borderRadius="4px"
          align="center"
          justify="center"
          bg="rgba(99,102,241,.11)"
          color="brand.400"
        >
          <Box as={loading ? Activity : Database} boxSize="5" />
        </Flex>
        <Text fontWeight="700">
          {loading
            ? "Loading real broker history"
            : runtimeInactive
              ? `${botStatus ?? "Selected"} bot is not connected to market data`
            : "Broker history is not available yet"}
        </Text>
        <Text fontSize="sm" color="fg.muted">
          {error
            ? `The server could not read cTrader candles: ${error}`
            : runtimeInactive
              ? "Start or resume this bot to open its cTrader market-data adapter. The monitor does not substitute another bot's candles."
            : "The chart stays empty until cTrader returns actual candles. No fallback or generated data is used."}
        </Text>
        <HStack gap="2" flexWrap="wrap" justify="center">
          <StatusPill
            tone={feedLabel === "FEED ACTIVE" ? "positive" : "warning"}
          >
            {feedLabel}
          </StatusPill>
          {lastMarketEvent && (
            <StatusPill>{formatTimestamp(lastMarketEvent)}</StatusPill>
          )}
        </HStack>
      </Stack>
    </Flex>
  );
}

function formatPrice(value?: number) {
  if (value === undefined || !Number.isFinite(value)) return "—";
  return value.toLocaleString("en-US", {
    minimumFractionDigits: value >= 100 ? 2 : 4,
    maximumFractionDigits: value >= 100 ? 2 : 4,
  });
}

function formatTimestamp(value?: string, compact = false) {
  if (!value) return "—";
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) return "—";
  return parsed.toLocaleString(
    [],
    compact
      ? { hour: "2-digit", minute: "2-digit" }
      : { dateStyle: "medium", timeStyle: "short" },
  );
}

function StateRow({ label, value }: { label: string; value: ReactNode }) {
  return (
    <Flex
      py="2.5"
      borderBottomWidth="1px"
      borderColor="border.subtle"
      justify="space-between"
      gap="3"
    >
      <Text fontSize="xs" color="fg.muted">
        {label}
      </Text>
      <Box fontSize="xs" fontWeight="700" textAlign="right">
        {value}
      </Box>
    </Flex>
  );
}

function RiskLine({
  label,
  value,
  state,
}: {
  label: string;
  value: string;
  state: string;
}) {
  return (
    <Flex
      py="2.5"
      borderBottomWidth="1px"
      borderColor="border.subtle"
      justify="space-between"
      align="center"
      gap="4"
    >
      <Box>
        <Text fontSize="xs" fontWeight="650">
          {label}
        </Text>
        <Text fontSize="10px" color="fg.muted">
          {state}
        </Text>
      </Box>
      <Text fontFamily="mono" fontSize="xs" fontWeight="800">
        {value}
      </Text>
    </Flex>
  );
}

function CompactPositions({ rows }: { rows: PositionRecord[] }) {
  if (rows.length === 0)
    return (
      <MonitorEmpty
        icon={Layers3}
        title="No open positions"
        description="The persisted broker account is currently flat."
      />
    );
  return (
    <Stack gap="2">
      {rows.map((row) => (
        <Box key={row.id} p="3" bg="bg.subtle" borderRadius="4px">
          <Flex justify="space-between">
            <HStack gap="2">
              <StatusPill tone={row.side === "LONG" ? "positive" : "negative"}>
                {row.side}
              </StatusPill>
              <Text fontWeight="700" fontSize="xs">
                {row.symbol}
              </Text>
            </HStack>
            <Money value={row.pnl} signed />
          </Flex>
          <Flex mt="2" justify="space-between">
            <Text fontSize="10px" color="fg.muted">
              {row.bot} · {row.quantity} lots
            </Text>
            <Text fontSize="10px" fontFamily="mono" color="fg.muted">
              {row.exposure.toFixed(2)}x
            </Text>
          </Flex>
        </Box>
      ))}
    </Stack>
  );
}

function CompactOrders({ rows }: { rows: OrderRecord[] }) {
  if (rows.length === 0)
    return (
      <MonitorEmpty
        icon={History}
        title="No recent orders"
        description="Broker-confirmed orders will appear here after execution."
      />
    );
  return (
    <Stack gap="2">
      {rows.slice(0, 4).map((row) => (
        <Flex
          key={row.id}
          p="3"
          bg="bg.subtle"
          borderRadius="4px"
          gap="3"
          align="center"
        >
          <Flex
            boxSize="28px"
            borderRadius="4px"
            align="center"
            justify="center"
            bg={
              row.side === "BUY"
                ? "rgba(16,185,129,.12)"
                : "rgba(244,63,94,.12)"
            }
          >
            <Box
              as={row.side === "BUY" ? ArrowUp : ArrowDown}
              boxSize="3.5"
              color={row.side === "BUY" ? "status.positive" : "status.negative"}
            />
          </Flex>
          <Box flex="1">
            <Text fontSize="xs" fontWeight="700">
              {row.side} {row.quantity} {row.symbol}
            </Text>
            <Text fontSize="10px" color="fg.muted">
              {row.sleeve} · {new Date(row.time).toLocaleString()}
            </Text>
          </Box>
          <StatusPill
            tone={
              row.status === "FILLED"
                ? "positive"
                : row.status === "REJECTED"
                  ? "negative"
                  : "neutral"
            }
          >
            {row.status}
          </StatusPill>
        </Flex>
      ))}
    </Stack>
  );
}

function MonitorEmpty({
  icon,
  title,
  description,
}: {
  icon: typeof History;
  title: string;
  description: string;
}) {
  return (
    <Flex minH="120px" align="center" justify="center" textAlign="center">
      <Stack gap="2" align="center" maxW="360px">
        <Flex
          boxSize="36px"
          borderRadius="4px"
          align="center"
          justify="center"
          bg="bg.subtle"
          color="fg.muted"
        >
          <Box as={icon} boxSize="4" />
        </Flex>
        <Text fontSize="sm" fontWeight="700">
          {title}
        </Text>
        <Text fontSize="xs" color="fg.muted">
          {description}
        </Text>
      </Stack>
    </Flex>
  );
}

export function OrdersPage() {
  const [search, setSearch] = useState("");
  const [botFilter, setBotFilter] = useState("all");
  const [accountFilter, setAccountFilter] = useState("all");
  const [symbolFilter, setSymbolFilter] = useState("all");
  const [sideFilter, setSideFilter] = useState("all");
  const [statusFilter, setStatusFilter] = useState("all");
  const { result, query } = useList<OrderRecord>({
    resource: "orders",
    pagination: { currentPage: 1, pageSize: 10_000 },
    liveMode: "auto",
  });
  const allRows = result.data ?? [];
  const normalizedSearch = search.trim().toLowerCase();
  const unavailableAccount = "__account_not_recorded__";
  const rows = allRows.filter((row) => {
    const accountKey = row.account ?? unavailableAccount;
    const matchesSearch = !normalizedSearch || [
      row.symbol,
      row.clientOrderId,
      row.brokerOrderId,
      row.bot,
      row.botId,
      row.account,
      row.strategy,
    ].some((value) => String(value ?? "").toLowerCase().includes(normalizedSearch));
    return matchesSearch
      && (botFilter === "all" || row.botId === botFilter)
      && (accountFilter === "all" || accountKey === accountFilter)
      && (symbolFilter === "all" || row.symbol === symbolFilter)
      && (sideFilter === "all" || row.side === sideFilter)
      && (statusFilter === "all" || row.status === statusFilter);
  });
  const uniqueOptions = (values: string[]) => [...new Set(values)].sort((a, b) => a.localeCompare(b));
  const botOptions = [...new Map(allRows.map((row) => [row.botId, row.bot])).entries()]
    .sort((a, b) => a[1].localeCompare(b[1]));
  const accountOptions = uniqueOptions(allRows.map((row) => row.account ?? unavailableAccount));
  const symbolOptions = uniqueOptions(allRows.map((row) => row.symbol));
  const sideOptions = uniqueOptions(allRows.map((row) => row.side));
  const statusOptions = uniqueOptions(allRows.map((row) => row.status));
  const activeFilters = [
    normalizedSearch,
    botFilter !== "all",
    accountFilter !== "all",
    symbolFilter !== "all",
    sideFilter !== "all",
    statusFilter !== "all",
  ].filter(Boolean).length;
  const clearFilters = () => {
    setSearch("");
    setBotFilter("all");
    setAccountFilter("all");
    setSymbolFilter("all");
    setSideFilter("all");
    setStatusFilter("all");
  };
  const cols: Column<OrderRecord>[] = [
    {
      key: "time",
      header: "Time",
      render: (row) => (
        <Text fontFamily="mono">{new Date(row.time).toLocaleString()}</Text>
      ),
    },
    {
      key: "bot",
      header: "Bot",
      mobilePriority: "primary",
      render: (row) => (
        <Box minW="170px">
          <Text fontWeight="700" lineClamp="1" title={row.bot}>{row.bot}</Text>
          <Text fontSize="9px" color="fg.subtle" fontFamily="mono" lineClamp="1" title={row.botId}>
            {row.botId}
          </Text>
        </Box>
      ),
    },
    {
      key: "account",
      header: "Account",
      hideBelow: "lg",
      render: (row) => (
        <Stack gap="0.5" minW="110px">
          <Text fontFamily="mono" fontWeight="700">{row.account ?? "Not recorded"}</Text>
          <Text fontSize="9px" color="fg.subtle">
            {[row.broker, row.brokerEnvironment, row.executionMode].filter(Boolean).join(" · ")}
          </Text>
        </Stack>
      ),
    },
    {
      key: "symbol",
      header: "Symbol",
      render: (row) => <Text fontWeight="700">{row.symbol}</Text>,
    },
    {
      key: "side",
      header: "Side",
      render: (row) => (
        <StatusPill tone={row.side === "BUY" ? "positive" : "negative"}>
          {row.side}
        </StatusPill>
      ),
    },
    { key: "type", header: "Type" },
    {
      key: "quantity",
      header: "Qty",
      numeric: true,
      render: (row) => <Text fontFamily="mono">{row.quantity}</Text>,
    },
    {
      key: "fillPrice",
      header: "Fill",
      numeric: true,
      render: (row) => (
        <Text fontFamily="mono">{row.fillPrice?.toFixed(2) ?? "—"}</Text>
      ),
    },
    {
      key: "status",
      header: "Status",
      render: (row) => (
        <StatusPill
          tone={
            row.status === "FILLED"
              ? "positive"
              : row.status === "REJECTED"
                ? "negative"
                : "neutral"
          }
        >
          {row.status}
        </StatusPill>
      ),
    },
    {
      key: "clientOrderId",
      header: "Client Order ID",
      hideBelow: "xl",
      render: (row) => (
        <Text
          maxW="260px"
          truncate
          title={row.clientOrderId}
          fontFamily="mono"
          color="fg.muted"
        >
          {row.clientOrderId}
        </Text>
      ),
    },
  ];
  return (
    <Stack gap="5">
      <PageHeader
        title="Orders"
        description="Durable order intents and broker results from SQLite."
        actions={<StatusPill tone="info">{allRows.length} PERSISTED</StatusPill>}
      />
      <Panel
        title="Order filters"
        icon={Filter}
        action={
          <Flex align="center" gap="2">
            <StatusPill tone={activeFilters > 0 ? "info" : "neutral"}>{activeFilters} ACTIVE</StatusPill>
            <Button size="xs" variant="ghost" disabled={activeFilters === 0} onClick={clearFilters}>
              <Box as={X} boxSize="3.5" />
              Clear
            </Button>
          </Flex>
        }
        p="4"
      >
        <Grid templateColumns={{ base: "1fr", md: "1fr 1fr", xl: "minmax(260px,2fr) repeat(5,minmax(130px,1fr))" }} gap="3">
          <OrderFilter label="Search orders">
            <HStack
              flex="1"
              bg="bg.panel"
              px="3"
              borderRadius="4px"
              borderWidth="1px"
              borderColor="border.default"
              _focusWithin={{ borderColor: "brand.500" }}
            >
              <Box as={Search} boxSize="4" color="fg.muted" />
              <Input
                aria-label="Search persisted orders"
                border="0"
                bg="transparent"
                value={search}
                onChange={(event) => setSearch(event.target.value)}
                placeholder="ID, bot, account or strategy"
              />
            </HStack>
          </OrderFilter>
          <OrderFilter label="Bot">
            <AppSelect ariaLabel="Filter orders by bot" value={botFilter} onValueChange={setBotFilter} options={[{ value: "all", label: "All bots" }, ...botOptions.map(([value, label]) => ({ value, label }))]} />
          </OrderFilter>
          <OrderFilter label="Account">
            <AppSelect ariaLabel="Filter orders by account" value={accountFilter} onValueChange={setAccountFilter} options={[{ value: "all", label: "All accounts" }, ...accountOptions.map((value) => ({ value, label: value === unavailableAccount ? "Account not recorded" : value }))]} />
          </OrderFilter>
          <OrderFilter label="Symbol">
            <AppSelect ariaLabel="Filter orders by symbol" value={symbolFilter} onValueChange={setSymbolFilter} options={[{ value: "all", label: "All symbols" }, ...symbolOptions.map((value) => ({ value, label: value }))]} />
          </OrderFilter>
          <OrderFilter label="Side">
            <AppSelect ariaLabel="Filter orders by side" value={sideFilter} onValueChange={setSideFilter} options={[{ value: "all", label: "All sides" }, ...sideOptions.map((value) => ({ value, label: value }))]} />
          </OrderFilter>
          <OrderFilter label="Status">
            <AppSelect ariaLabel="Filter orders by status" value={statusFilter} onValueChange={setStatusFilter} options={[{ value: "all", label: "All statuses" }, ...statusOptions.map((value) => ({ value, label: value }))]} />
          </OrderFilter>
        </Grid>
      </Panel>
      <Panel
        title="Order ledger"
        icon={History}
        action={<StatusPill>{rows.length} RESULTS</StatusPill>}
      >
        <DataTable
          rows={rows}
          columns={cols}
          rowLabel="orders"
          empty={query.isLoading ? "Loading orders…" : activeFilters > 0 ? "No persisted orders match the active filters" : "No persisted orders"}
        />
      </Panel>
    </Stack>
  );
}

function OrderFilter({ label, children }: { label: string; children: ReactNode }) {
  return (
    <Stack gap="1.5">
      <Text fontSize="9px" color="fg.subtle" fontWeight="800" letterSpacing=".05em" textTransform="uppercase">
        {label}
      </Text>
      {children}
    </Stack>
  );
}

export function PositionsPage() {
  const { open } = useNotification();
  const { result, query } = useList<PositionRecord>({
    resource: "positions",
    pagination: { currentPage: 1, pageSize: 10_000 },
    liveMode: "auto",
  });
  const summaryQuery = useCustom<PortfolioSummary>({
    url: "/portfolio/summary",
    method: "get",
    queryOptions: { refetchInterval: 5_000 },
  });
  const rows = result.data ?? [];
  const summary = summaryQuery.query.data?.data;
  const [flattening, setFlattening] = useState(false);
  const flatten = async () => {
    if (
      !window.confirm(
        "Flatten every active bot position and persistently pause those bots?",
      )
    )
      return;
    setFlattening(true);
    try {
      const affected = await apiRequest<Array<{ id: string }>>(
        "/trading/positions/flatten-all",
        { method: "POST", body: JSON.stringify({ reason: "UI_FLATTEN_ALL" }) },
      );
      open?.({
        type: "success",
        message: "Flatten completed",
        description: `${affected.length} active bots were flattened and paused.`,
      });
      await Promise.all([query.refetch(), summaryQuery.query.refetch()]);
    } catch (error) {
      open?.({
        type: "error",
        message: "Flatten failed",
        description: error instanceof Error ? error.message : String(error),
      });
    } finally {
      setFlattening(false);
    }
  };
  const cols: Column<PositionRecord>[] = [
    {
      key: "symbol",
      header: "Symbol",
      render: (row) => <Text fontWeight="750">{row.symbol}</Text>,
    },
    {
      key: "side",
      header: "Side",
      render: (row) => (
        <StatusPill tone={row.side === "LONG" ? "positive" : "negative"}>
          {row.side}
        </StatusPill>
      ),
    },
    { key: "quantity", header: "Qty", numeric: true },
    {
      key: "entry",
      header: "Avg entry",
      numeric: true,
      render: (row) => <Text fontFamily="mono">{row.entry.toFixed(2)}</Text>,
    },
    {
      key: "mark",
      header: "Mark",
      numeric: true,
      render: (row) => <Text fontFamily="mono">{row.mark.toFixed(2)}</Text>,
    },
    {
      key: "pnl",
      header: "Unrealized PnL",
      numeric: true,
      render: (row) => <Money value={row.pnl} signed />,
    },
    {
      key: "exposure",
      header: "Exposure",
      numeric: true,
      render: (row) => (
        <Text fontFamily="mono">{row.exposure.toFixed(2)}x</Text>
      ),
    },
    { key: "bot", header: "Account", hideBelow: "md" },
  ];
  return (
    <Stack gap="5">
      <PageHeader
        title="Positions"
        description="Persisted execution exposure aggregated across every registered bot account."
        actions={
          <>
            <StatusPill tone={query.isLoading ? "neutral" : rows.length === 0 ? "positive" : "warning"}>
              {query.isLoading ? "SYNCING" : rows.length === 0 ? "PORTFOLIO FLAT" : `${rows.length} OPEN`}
            </StatusPill>
            <Button
              size="sm"
              colorPalette="red"
              variant="outline"
              onClick={flatten}
              disabled={flattening || rows.length === 0}
            >
              <Box as={AlertTriangle} boxSize="4" />
              {flattening ? "Flattening…" : "Flatten all"}
            </Button>
          </>
        }
      />
      <Grid templateColumns={{ base: "1fr 1fr", lg: "repeat(4,1fr)" }} gap="3">
        <MetricCard
          icon={Layers3}
          label="Open positions"
          value={query.isLoading ? "—" : String(rows.length)}
          detail={query.isLoading ? "Waiting for server state" : rows.length === 0 ? "No active market exposure" : "Across all bot accounts"}
        />
        <MetricCard
          icon={CircleDollarSign}
          label="Unrealized PnL"
          value={
            query.isLoading ? "—" : <Money value={rows.reduce((sum, row) => sum + row.pnl, 0)} signed />
          }
          detail={query.isLoading ? "Waiting for server state" : "Live sum across open positions"}
        />
        <MetricCard
          icon={Gauge}
          label="Gross exposure"
          value={summary ? `${summary.grossExposure.toFixed(2)}x` : "—"}
          detail={summary ? `${summary.maxLeverage.toFixed(0)}x configured ceiling` : "Waiting for portfolio snapshot"}
        />
        <MetricCard
          icon={WalletCards}
          label="Margin utilization"
          value={summary ? `${summary.marginUtilization.toFixed(2)}%` : "—"}
          detail={summary ? "Execution account margin / equity" : "Waiting for portfolio snapshot"}
        />
      </Grid>
      <Grid
        templateColumns={{ base: "1fr", lg: "minmax(0,1.55fr) minmax(300px,.65fr)" }}
        gap="4"
        alignItems="start"
      >
        <Panel
          icon={Layers3}
          title="Position ledger"
          action={<StatusPill>{rows.length} OPEN</StatusPill>}
        >
          {rows.length === 0 && !query.isLoading ? (
            <EmptyState
              icon={ShieldCheck}
              title="Portfolio is flat"
              description="The latest persisted execution account snapshot contains no open positions. Strategies can remain active while waiting for a valid entry signal."
              action={
                <HStack gap="2" flexWrap="wrap" justify="center">
                  <Button asChild size="sm" variant="outline">
                    <Link to="/trading/live">
                      <Box as={Radio} boxSize="4" />
                      Open live monitor
                    </Link>
                  </Button>
                  <Button asChild size="sm" variant="ghost">
                    <Link to="/bots">
                      <Box as={Bot} boxSize="4" />
                      Review bots
                    </Link>
                  </Button>
                </HStack>
              }
            />
          ) : (
            <DataTable
              rows={rows}
              columns={cols}
              rowLabel="positions"
              empty="Loading positions…"
            />
          )}
        </Panel>
        <Stack gap="4">
          <Panel
            icon={Gauge}
            title="Risk utilization"
            action={<StatusPill tone="positive">SAFE</StatusPill>}
          >
            <Stack gap="4">
              <PositionUtilization
                label="Gross exposure"
                value={summary?.grossExposure}
                max={summary?.maxLeverage}
                suffix="x"
              />
              <PositionUtilization
                label="Net exposure"
                value={summary ? Math.abs(summary.netExposure) : undefined}
                max={summary?.maxLeverage}
                suffix="x"
              />
              <PositionUtilization
                label="Margin utilization"
                value={summary?.marginUtilization}
                max={summary ? 100 : undefined}
                suffix="%"
              />
              <PositionUtilization
                label="Current drawdown"
                value={summary?.currentDrawdown}
                max={summary?.maxDrawdownLimit}
                suffix="%"
              />
            </Stack>
          </Panel>
          <Panel icon={Database} title="Snapshot provenance">
            <Stack gap="0">
              <StateRow label="Source" value="Persisted server state" />
              <StateRow
                label="Bot accounts"
                value={summary ? String(summary.accountCount) : "—"}
              />
              <StateRow
                label="Last updated"
                value={formatTimestamp(summary?.asOf)}
              />
            </Stack>
            <Text mt="3" fontSize="10px" color="fg.subtle" lineHeight="1.6">
              Paper balances are simulated ledgers. Position and exposure values shown here are never generated as fallback data.
            </Text>
          </Panel>
        </Stack>
      </Grid>
    </Stack>
  );
}

function PositionUtilization({
  label,
  value,
  max,
  suffix,
}: {
  label: string;
  value?: number;
  max?: number;
  suffix: string;
}) {
  const hasSnapshot = Number.isFinite(value) && Number.isFinite(max);
  const currentValue = hasSnapshot ? value! : 0;
  const maximumValue = hasSnapshot ? max! : 1;
  return (
    <Box>
      <Flex justify="space-between" align="baseline" gap="3">
        <Text fontSize="xs" color="fg.muted">
          {label}
        </Text>
        <Text fontSize="xs" fontFamily="mono" fontWeight="750">
          {hasSnapshot ? `${currentValue.toFixed(2)}${suffix} / ${maximumValue.toFixed(2)}${suffix}` : "Waiting for snapshot"}
        </Text>
      </Flex>
      <Box mt="2">
        <DataBar value={currentValue} max={maximumValue} tone="positive" />
      </Box>
    </Box>
  );
}
